import './style.css'
import './svg-controls.css'
import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import CustomInput from './CustomInput';
import ValidityPeriodInput from './ValidityPeriodInput';
import React from 'react';
import { ReactComponent as Doctor } from '../../../../../assets/img/pharmacy/DOCTOR.svg';
import { ReactComponent as MedicalCommission } from '../../../../../assets/img/pharmacy/medicalCommission.svg';
import { ReactComponent as Recipe } from '../../../../../assets/img/pharmacy/recipe.svg';
import { ReactComponent as Stamp } from '../../../../../assets/img/pharmacy/stamp.svg';
import { ReactComponent as Stamp1 } from '../../../../../assets/img/pharmacy/Stamp1.svg';
import { ReactComponent as Stamp2 } from '../../../../../assets/img/pharmacy/Stamp2.svg';
import { ReactComponent as Stamp4 } from '../../../../../assets/img/pharmacy/Stamp4.svg';
import { ReactComponent as Special1 } from '../../../../../assets/img/pharmacy/Special1.svg';
import { ReactComponent as Special2 } from '../../../../../assets/img/pharmacy/Special2.svg';
import { ReactComponent as Special5 } from '../../../../../assets/img/pharmacy/Special5.svg';
import { ReactComponent as Recipe1 } from '../../../../../assets/img/pharmacy/Recipe1.svg';
import { ReactComponent as Doctor1 } from '../../../../../assets/img/pharmacy/Doctor1.svg';

interface Form107Props {
  onInputChange?: (id: number, value: string) => void;
  initialValues: InitialValuesType;
  onCheckboxStateChange?: (inputCheckboxStates: {
    inputs: { [key: number]: boolean };
    svgs: { [key: number]: boolean };
  }) => void;
  isShow?: boolean;
  isReadOnly?: boolean;
  isShowDeleteButton?: boolean;
  formDroppedItems?: SvgType[];
  handleDragStart?: (index: number, type: string) => void;
  handleDragOver?: () => void;
  handleDrop?: (event: React.DragEvent<HTMLDivElement>) => void;
  onSvgDelete?: (index: number, id: number) => void;
  isConstructor?: boolean;
  isChatPage?: boolean;
  isPharmacyModal?: boolean;
  userValidityInput?: string;
  onUserValidityInputChange?: (input: string) => void;
}

interface InitialValuesType {
  inputs?: {
    [key: number]: {
      value: string | undefined;
      right?: boolean;
      [key: string]: any;
    };
  };
  svgs?: Array<SvgType>;
}

interface SvgType {
  id: number;
  type: string;
  x: number;
  y: number;
  isWrongAnswer: boolean;
}

const Form107 = forwardRef(({
  initialValues,
  onInputChange,
  formDroppedItems = [],
  handleDragStart,
  handleDragOver,
  handleDrop,
  onSvgDelete,
  isShow,
  isReadOnly,
  onCheckboxStateChange,
  isConstructor,
  isChatPage,
  isPharmacyModal,
  userValidityInput,
  onUserValidityInputChange
}: Form107Props, ref) => {
  const [inputCheckboxStates, setInputCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    const initialCheckboxStates = initialValues?.inputs 
      ? Object.fromEntries(
          Object.entries(initialValues.inputs).map(([id, input]) => [
            Number(id),
            input.right || false,
          ])
        )
      : {};
    return initialCheckboxStates;
  });

  const [svgCheckboxStates, setSvgCheckboxStates] = useState<{ [key: number]: boolean }>(() => {
    const initialSvgCheckboxStates = initialValues?.svgs
      ? Object.fromEntries(
          initialValues.svgs.map((svg) => [svg.id, svg.isWrongAnswer || false])
        )
      : {};
    return initialSvgCheckboxStates;
  });

  const [values, setValues] = useState(() => ({
    inputs: initialValues?.inputs || {},
  }));

  useEffect(() => {
    setValues((prevValues) => ({
      inputs: {
        ...prevValues.inputs,
        ...initialValues.inputs,
      },
    }));
  }, [initialValues.inputs]);

  const resetAllFields = () => {
    setValues({ inputs: {} });
    setInputCheckboxStates({});
    setSvgCheckboxStates({});
  };

  useImperativeHandle(ref, () => ({
    resetAllFields,
  }));

  const handleInputChangeLocal = (id: number, value: string) => {
    setValues((prev) => ({
      ...prev,
      inputs: {
        ...prev.inputs,
        [id]: {
          ...prev.inputs[id],
          value,
        },
      },
    }));
    onInputChange?.(id, value);
  };

  const handleInputCheckboxChange = (id: number) => {
    setInputCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      setValues((prevValues) => ({
        inputs: {
          ...prevValues.inputs,
          [id]: {
            ...prevValues.inputs[id],
            right: newState[id],
          },
        },
      }));

      onCheckboxStateChange?.({ inputs: newState, svgs: svgCheckboxStates });
      return newState;
    });
  };

  const handleSvgCheckboxChange = (id: number) => {
    setSvgCheckboxStates((prev) => {
      const newState = { ...prev, [id]: !prev[id] };

      onCheckboxStateChange?.({ inputs: inputCheckboxStates, svgs: newState });
      return newState;
    });
  };

  const getSvgStyle = (type: string, x: number, y: number) => {
    const baseStyle = {
      position: 'absolute' as const,
      cursor: 'move'
    };

    switch (type) {
      case 'Doctor':
        return {
          ...baseStyle,
          left: `${x - 30}px`,
          top: `${y + 40}px`,
          transform: 'scale(1)',
        };
      case 'MedicalCommission':
        return {
          ...baseStyle,
          left: `${x - 10}px`,
          top: `${y + 70}px`,
          transform: 'scale(1.1)',
        };
      case 'Recipe':
        return {
          ...baseStyle,
          left: `${x - 110}px`,
          top: `${y + 60}px`,
          transform: 'scale(1)',
        };
      case 'Stamp':
        return {
          ...baseStyle,
          left: `${x - 200}px`,
          top: `${y - 40}px`,
          transform: 'scale(1.2)',
        };
      case 'Stamp1':
        return {
          ...baseStyle,
          left: `${x - 190}px`,
          top: `${y + 95}px`,
          transform: 'scale(1)',
        };
      case 'Stamp2':
        return {
          ...baseStyle,
          left: `${x - 100}px`,
          top: `${y + 50}px`,
          transform: 'scale(1.3)',
        };
      case 'Stamp4':
        return {
          ...baseStyle,
          left: `${x - 15}px`,
          top: `${y + 55}px`,
          transform: 'scale(0.5)',
        };
      case 'Special1':
        return {
          ...baseStyle,
          left: `${x - 180}px`,
          top: `${y + 50}px`,
          transform: 'scale(0.6)',
        };
      case 'Special2':
        return {
          ...baseStyle,
          left: `${x - 70}px`,
          top: `${y -10}px`,
          transform: 'scale(0.6)',
        };
      case 'Special5':
        return {
          ...baseStyle,
          left: `${x - 80}px`,
          top: `${y - 50}px`,
          transform: 'scale(0.5)',
        };
      case 'Recipe1':
        return {
          ...baseStyle,
          left: `${x +100}px`,
          top: `${y - 60}px`,
          transform: 'scale(1.3)',
        };
      case 'Doctor1':
        return {
          ...baseStyle,
          left: `${x + 140}px`,
          top: `${y - 100}px`,
          transform: 'scale(1.1)',
        };
      default:
        return {
          ...baseStyle,
          left: `${x}px`,
          top: `${y}px`,
          transform: 'scale(2)',
        };
    }
  };

  return (
    <div
      style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '20px',
        position: 'relative',
        border: '1px solid rgba(34, 34, 34, 0.31)'
      }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {[...initialValues.svgs || [], ...formDroppedItems].map((svg, index) => (
        <div
          key={`svg-${svg.id}-${index}`}
          className="svg-wrapper"
          style={getSvgStyle(svg.type, svg.x, svg.y)}
          draggable={isConstructor}
          onDragStart={() => handleDragStart?.(index, 'response')}
        >
          {svg.type === 'Doctor' && <Doctor />}
          {svg.type === 'MedicalCommission' && <MedicalCommission />}
          {svg.type === 'Recipe' && <Recipe />}
          {svg.type === 'Stamp' && <Stamp />}
          {svg.type === 'Stamp1' && <Stamp1 />}
          {svg.type === 'Stamp2' && <Stamp2 />}
          {svg.type === 'Stamp4' && <Stamp4 />}
          {svg.type === 'Special1' && <Special1 />}
          {svg.type === 'Special2' && <Special2 />}
          {svg.type === 'Special5' && <Special5 />}
          {svg.type === 'Recipe1' && <Recipe1 />}
          {svg.type === 'Doctor1' && <Doctor1 />}
          <div className="svg-controls">
            {isShow && (
              <input
                type="checkbox"
                className="svg-checkbox"
                checked={svgCheckboxStates[svg.id] || false}
                onChange={() => handleSvgCheckboxChange(svg.id)}
              />
            )}
            {isConstructor && (
              <button
                className="svg-delete-button"
                onClick={() => onSvgDelete?.(index, svg.id)}
              >
                ×
              </button>
            )}
          </div>
        </div>
      ))}

      <div className="header" style={{ textAlign: 'right', marginBottom: '20px', lineHeight: 1.2 }}>
        Приложение 4<br />
        к приказу Министерства здравоохранения<br />
        Российской Федерации<br />
        от 24 ноября 2021 г. N 1094н<br />
      </div>

      <div style={{ fontSize: '12pt', textAlign: 'center', marginBottom: '10px' }}>
        ФОРМА СПЕЦИАЛЬНОГО РЕЦЕПТУРНОГО БЛАНКА N 107/У-НП
      </div>

      <div style={{ borderCollapse: 'collapse', fontSize: '12pt', display: 'flex', justifyContent: 'center' }}>
        <div style={{ display: 'flex' }}>
          <div style={{
            border: '1px solid #8e8e8ea2',
            width: '50%',
            verticalAlign: 'top',
            padding: '5px',
            lineHeight: 1.2
          }}>
            <div>Министерство здравоохранения<br />Российской Федерации</div>
            <div style={{ height: '55px' }}></div>
            <div style={{ textAlign: 'right' }}>штамп медицинской организации</div>
          </div>
          <div style={{
            border: '1px solid #8e8e8ea2',
            width: '50%',
            verticalAlign: 'top',
            padding: '5px',
            lineHeight: 1.2
          }}>
            <div>Код формы по ОКУД</div>
            <div>Медицинская документация</div>
            <div>Форма N 107/у-НП,</div>
            <div>утвержденная приказом</div>
            <div>Министерства здравоохранения</div>
            <div>Российской Федерации</div>
            <div>от 24.11.2021 N 1094н</div>
          </div>
        </div>
      </div>

      <div className="recipe">
        <div style={{ marginTop: '5px', fontSize: '14pt', textAlign: 'center' }}>РЕЦЕПТ</div>
        <div style={{ marginTop: '5px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <label>Серия</label>
            <CustomInput
              id={1}
              onChange={handleInputChangeLocal}
              inputCount={4}
              value={values.inputs?.[1]?.value ?? ''}
              disabled={isReadOnly}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
            <label>N</label>
            <CustomInput
              id={2}
              onChange={handleInputChangeLocal}
              inputCount={5}
              value={values.inputs?.[2]?.value ?? ''}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[2] || false}
                onChange={() => handleInputCheckboxChange(2)}
              />
            )}
          </div>
        </div>
        <div
          style={{
            marginTop: '10px',
            height: '20px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginRight: '200px'
          }}
        >
          <p>"</p>
          <input
            type="text"
            style={{ width: '30px', border: 'none', borderBottom: '1px solid #000', textAlign: 'center', fontSize: '12pt' }}
            value={values.inputs?.[3]?.value ?? ''}
            onChange={e => {
              const val = e.target.value.replace(/\\D/g, '').slice(0, 2);
              handleInputChangeLocal(3, val);
            }}
            disabled={isReadOnly}
            maxLength={2}
            inputMode="numeric"
            pattern="\\d*"
          />
          <p>"</p>
          <input
            type="text"
            style={{ width: '100px', border: 'none', borderBottom: '1px solid #000', textAlign: 'center', fontSize: '12pt' }}
            value={values.inputs?.[4]?.value ?? ''}
            onChange={(e) => {
              const val = e.target.value.slice(0, 8);
              handleInputChangeLocal(4, val);
            }}
            disabled={isReadOnly}
          />
          <p>20</p>
          <input
            type="text"
            style={{ width: '30px', border: 'none', borderBottom: '1px solid #000', textAlign: 'center', fontSize: '12pt' }}
            value={values.inputs?.[5]?.value ?? ''}
            onChange={e => {
              const val = e.target.value.replace(/\\D/g, '').slice(0, 2);
              handleInputChangeLocal(5, val);
            }}
            disabled={isReadOnly}
            maxLength={2}
            inputMode="numeric"
            pattern="\\d*"
          />
          <p>г.</p>
          {isShow && (
            <input
              type="checkbox"
              checked={inputCheckboxStates[5] || false}
              onChange={() => handleInputCheckboxChange(5)}
            />
          )}
        </div>
        <div style={{ textAlign: 'right', marginRight: '245px' }}>(дата выписки рецепта)</div>
        <div style={{ textAlign: 'center', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span>(</span>
          <span 
            style={{ 
              textDecoration: values.inputs?.[16]?.value === 'adult' ? 'underline' : 'none',
              textDecorationThickness: '2px',
              textDecorationColor: '#000',
              textUnderlineOffset: '2px',
              cursor: 'pointer',
            }}
            onClick={() => !isReadOnly && handleInputChangeLocal(16, 'adult')}
          >
            взрослый
          </span>
          <span>, </span>
          <span 
            style={{ 
              textDecoration: values.inputs?.[16]?.value === 'child' ? 'underline' : 'none',
              textDecorationThickness: '2px',
              textDecorationColor: '#000',
              textUnderlineOffset: '2px',
              cursor: 'pointer',
              padding: '0px 4px'
            }}
            onClick={() => !isReadOnly && handleInputChangeLocal(16, 'child')}
          >
            детский
          </span>
          <span> - нужное подчеркнуть)</span>
          {isShow && (
            <input
              id="checkbox-16"
              type="checkbox"
              checked={inputCheckboxStates[16] || false}
              onChange={() => handleInputCheckboxChange(16)}
            />
          )}
        </div>

        <div className="field">
          <label>Фамилия, имя, отчество (последнее - при наличии) пациента</label>
          <div>
            <input
              type="text"
              style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[6]?.value ?? ''}
              onChange={(e) => handleInputChangeLocal(6, e.target.value)}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[6] || false}
                onChange={() => handleInputCheckboxChange(6)}
              />
            )}
          </div>
        </div>

        <div className="field" style={{ marginBottom: '15px' }}>
          <label>Возраст</label>
          <input
            type="text"
            style={{ marginLeft: '10px', width: '89%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
            value={values.inputs?.[7]?.value ?? ''}
            onChange={(e) => handleInputChangeLocal(7, e.target.value)}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              type="checkbox"
              checked={inputCheckboxStates[7] || false}
              onChange={() => handleInputCheckboxChange(7)}
            />
          )}
        </div>

        <div className="field">
          <label>Серия и номер полиса обязательного медицинского страхования</label>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
            <input
              type="text"
              style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[8]?.value ?? ''}
              onChange={(e) => handleInputChangeLocal(8, e.target.value)}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[8] || false}
                onChange={() => handleInputCheckboxChange(8)}
              />
            )}
          </div>
        </div>

        <div className="field">
          <label>Номер медицинской карты</label>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start' }}>
            <input
              type="text"
              style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[9]?.value ?? ''}
              onChange={(e) => handleInputChangeLocal(9, e.target.value)}
              disabled={isReadOnly}
            />
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[9] || false}
                onChange={() => handleInputCheckboxChange(9)}
              />
            )}
          </div>
        </div>

        <div className="field">
          <label>Фамилия, имя, отчество (последнее - при наличии) лечащего врача (фельдшера, акушерки)</label>
          <input
            type="text"
            value={values.inputs?.[10]?.value ?? ''}
            onChange={(e) => handleInputChangeLocal(10, e.target.value)}
            disabled={isReadOnly}
            style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
          />
          {isShow && (
            <input
              type="checkbox"
              checked={inputCheckboxStates[10] || false}
              onChange={() => handleInputCheckboxChange(10)}
            />
          )}
        </div>

        <div style={{ margin: '20px 0', display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
          <p>
            Rp:
          </p>
          <input
            type="text"
            style={{ border: 'none', borderBottom: '3px dotted black', width: '95%', height: '25px', fontSize: '14pt' }}
            value={values.inputs?.[11]?.value ?? ''}
            onChange={(e) => handleInputChangeLocal(11, e.target.value)}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              type="checkbox"
              checked={inputCheckboxStates[11] || false}
              onChange={() => handleInputCheckboxChange(11)}
            />
          )}
        </div>

        <div className="field" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '0px 0 10px 0', width: '100%' }}>
          <label>Подпись и личная печать врача (подпись фельдшера, акушерки)</label>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <input
              type="text"
              style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[12]?.value ?? ''}
              onChange={(e) => handleInputChangeLocal(12, e.target.value)}
              disabled={isReadOnly}
            />
            <span style={{ textAlign: 'right', marginLeft: '10px' }}>М.П.</span>
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[12] || false}
                onChange={() => handleInputCheckboxChange(12)}
              />
            )}
          </div>
        </div>

        <div className="field" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '0px 0 10px 0', width: '100%' }}>
          <label>
            Фамилия, имя, отчество (последнее - при наличии) и подпись уполномоченного лица медицинской организации
          </label>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <input
              type="text"
              style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[13]?.value ?? ''}
              onChange={(e) => handleInputChangeLocal(13, e.target.value)}
              disabled={isReadOnly}
            />
            <span style={{ textAlign: 'right', marginLeft: '10px' }}>М.П.</span>
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[13] || false}
                onChange={() => handleInputCheckboxChange(13)}
              />
            )}
          </div>
        </div>

        <div className="field" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <label style={{ width: '60%' }}>Отметка аптечной организации об отпуске</label>
          <input
            type="text"
            style={{ width: '40%', border: 'none', borderBottom: '1px solid #000', fontSize: '14pt', marginLeft: '10px', height: '25px' }}
            value={values.inputs?.[14]?.value ?? ''}
            onChange={(e) => handleInputChangeLocal(14, e.target.value)}
            disabled={isReadOnly}
          />
          {isShow && (
            <input
              type="checkbox"
              checked={inputCheckboxStates[14] || false}
              onChange={() => handleInputCheckboxChange(14)}
            />
          )}
        </div>

        <div className="field" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', margin: '0px 0 10px 0', width: '100%' }}>
          <label>
            Фамилия, имя, отчество (последнее - при наличии) и подпись работника аптечной организации
          </label>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}>
            <input
              type="text"
              style={{ width: '95%', border: 'none', borderBottom: '1px solid #000', height: 25, fontSize: '14pt' }}
              value={values.inputs?.[15]?.value ?? ''}
              onChange={(e) => handleInputChangeLocal(15, e.target.value)}
              disabled={isReadOnly}
            />
            <span style={{ width: '10%', textAlign: 'right', marginLeft: '10px' }}>М.П.</span>
            {isShow && (
              <input
                type="checkbox"
                checked={inputCheckboxStates[15] || false}
                onChange={() => handleInputCheckboxChange(15)}
              />
            )}
          </div>
        </div>
      </div>

      <div style={{ textAlign: 'left', fontSize: '12pt', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
        <ValidityPeriodInput
          value={values.inputs?.[99]?.value ?? ''}
          onChange={handleInputChangeLocal}
          isReadOnly={isReadOnly}
          isChatPage={isChatPage}
          isPharmacyModal={isPharmacyModal}
          userValidityInput={userValidityInput}
          onUserValidityInputChange={onUserValidityInputChange}
          showCheckbox={isShow}
          onCheckboxChange={(checked) => handleInputCheckboxChange(99)}
          isCheckboxChecked={inputCheckboxStates[99] || false}
        />
      </div>
    </div>
  );
});

export default Form107;
